<template>
    <div class="quick-auth-overlay">
        <div class="quick-auth-modal">
            <div class="logo-section">
                <img :src="props.logoPath" alt="Logo" class="logo" />
            </div>
            <h2 class="title">{{ props.appTitle }}</h2>
            <p class="subtitle">{{ props.subtitle }}</p>

            <!-- Google 登入按鈕 -->
            <button
                class="google-login-btn"
                @click="handleGoogleLogin"
                :disabled="loading"
            >
                <span v-if="loading" class="loading-content">
                    <span class="spinner"></span>
                    登入中...
                </span>
                <template v-else>
                    <svg class="google-icon" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span>使用 Google 帳號登入</span>
                </template>
            </button>

            <!-- 錯誤訊息 -->
            <p v-if="error" class="error-message">{{ error }}</p>

            <!-- 資訊訊息 -->
            <p v-if="infoMessage" class="info-message">{{ infoMessage }}</p>

            <p class="terms">
                登入即表示您同意系統使用條款與隱私權政策
            </p>

            <!-- 版本資訊 -->
            <div v-if="props.showVersion" class="version-info">
                <div>v{{ webVersion }} / {{ authEnv }}</div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, defineProps, withDefaults } from 'vue';
import { useSupabaseAuth } from '@shared/composables/useSupabaseAuth';

/**
 * QuickAuth Props 介面
 */
interface QuickAuthProps {
    /** 應用程式名稱（用於 useSupabaseAuth） */
    appName?: string;
    /** 顯示標題 */
    appTitle?: string;
    /** 副標題 */
    subtitle?: string;
    /** Logo 路徑 */
    logoPath?: string;
    /** OAuth 重定向路徑 */
    redirectPath?: string;
    /** 登入成功後跳轉的路由 */
    successRedirectRoute?: string;
    /** 是否顯示版本資訊 */
    showVersion?: boolean;
    /** 自訂版本號（若未設定則嘗試從 __APP_VERSION__ 取得） */
    version?: string;
}

// 定義 props 並設定預設值
const props = withDefaults(defineProps<QuickAuthProps>(), {
    appName: 'ppom',
    appTitle: '歡迎使用牧陽PPOM',
    subtitle: '智慧化電廠維運管理系統',
    logoPath: '/img/logo-2024-tb.png',
    redirectPath: '/',
    successRedirectRoute: '/patrol/FocusField',
    showVersion: true,
    version: ''
});

const localLoading = ref(false);
const localError = ref('');

// 版本資訊
const webVersion = ref(props.version || (typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '1.0.0'));
const authEnv = ref('Web');

// 檢查是否在原生平台（Capacitor）
const checkNativePlatform = async () => {
    try {
        // 動態導入 Capacitor，避免在沒有安裝時報錯
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.isNativePlatform()) {
            try {
                // 讀取 Android 建置時產生的 SHA-1 資訊
                const response = await fetch('/build-info.json');
                if (response.ok) {
                    const info = await response.json();
                    // 顯示格式: Android / SHA1前8碼
                    authEnv.value = `Android / ${info.sha1Short}`;
                } else {
                    authEnv.value = 'Android';
                }
            } catch {
                authEnv.value = 'Android';
            }
        }
    } catch {
        // Capacitor 未安裝，保持 Web 環境
    }
};

// 取得建置資訊（僅 Android）
onMounted(async () => {
    await checkNativePlatform();
});

const {
    isLoading,
    error: authError,
    infoMessage,
    signInWithGoogle
} = useSupabaseAuth({
    appName: props.appName,
    redirectPath: props.redirectPath,
    successRedirectRoute: props.successRedirectRoute
});

const handleGoogleLogin = async () => {
    console.log(`[QuickAuth:${props.appName}] handleGoogleLogin 被呼叫`);
    localLoading.value = true;
    localError.value = '';

    try {
        console.log(`[QuickAuth:${props.appName}] 正在呼叫 signInWithGoogle...`);
        await signInWithGoogle();
        console.log(`[QuickAuth:${props.appName}] signInWithGoogle 完成`);
    } catch (err: any) {
        console.error(`[QuickAuth:${props.appName}] 登入錯誤:`, err);
        console.error(`[QuickAuth:${props.appName}] 錯誤詳情:`, JSON.stringify(err, null, 2));
        localError.value = err.message || 'Google 登入失敗';
        // 顯示 alert 讓用戶看到錯誤
        alert('登入錯誤: ' + (err.message || JSON.stringify(err)));
    } finally {
        localLoading.value = false;
    }
};

// 暴露的狀態和方法
const loading = isLoading;
const error = authError;

// 宣告全域變數
declare const __APP_VERSION__: string;
</script>

<style scoped lang="stylus">
.quick-auth-overlay
    position: fixed
    inset: 0
    background: rgba(0, 0, 0, 0.6)
    display: flex
    align-items: center
    justify-content: center
    z-index: 9999
    padding: 20px

.quick-auth-modal
    background: #fff
    border-radius: 16px
    padding: 40px
    width: 100%
    max-width: 400px
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3)
    text-align: center

.logo-section
    margin-bottom: 20px
    display: flex
    justify-content: center

    .logo
        width: 80px
        height: auto

.title
    font-size: 24px
    font-weight: bold
    color: #333
    margin-bottom: 8px

.subtitle
    font-size: 14px
    color: #666
    margin-bottom: 32px

.google-login-btn
    width: 100%
    padding: 14px 20px
    background: #fff
    border: 1px solid #ddd
    border-radius: 8px
    font-size: 16px
    font-weight: 500
    color: #333
    cursor: pointer
    display: flex
    align-items: center
    justify-content: center
    gap: 12px
    transition: all 0.2s ease

    &:hover:not(:disabled)
        background: #f8f8f8
        border-color: #ccc
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1)

    &:disabled
        opacity: 0.6
        cursor: not-allowed

.google-icon
    width: 20px
    height: 20px

.loading-content
    display: flex
    align-items: center
    gap: 8px

.spinner
    width: 18px
    height: 18px
    border: 2px solid #ddd
    border-top-color: #4285F4
    border-radius: 50%
    animation: spin 1s linear infinite

@keyframes spin
    to
        transform: rotate(360deg)

.error-message
    margin-top: 16px
    padding: 12px
    background: #fff2f0
    border: 1px solid #ffccc7
    border-radius: 6px
    color: #ff4d4f
    font-size: 14px

.info-message
    margin-top: 16px
    padding: 12px
    background: #e6f7ff
    border: 1px solid #91d5ff
    border-radius: 6px
    color: #1890ff
    font-size: 14px

.terms
    margin-top: 24px
    font-size: 12px
    color: #999
    line-height: 1.5

.version-info
    margin-top: 16px
    font-size: 11px
    color: #bbb
    text-align: center

    .auth-env
        margin-top: 4px
        color: #ccc
</style>
